import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Upload, X, Check, ChevronsUpDown, Camera, Info, CircleDot, Diamond, Droplet, Loader2, Link2 } from 'lucide-react';

const PHOTO_SLOTS = [
  { key: 'front', labelKey: 'cl.photo_front', descKey: 'cl.photo_front_desc', required: true },
  { key: 'back', labelKey: 'cl.photo_back', descKey: 'cl.photo_back_desc', required: true },
  { key: 'profile', labelKey: 'cl.photo_profile', descKey: 'cl.photo_profile_desc', required: true },
  { key: 'grip', labelKey: 'cl.photo_grip', descKey: 'cl.photo_grip_desc', required: true },
  { key: 'detail', labelKey: 'cl.photo_detail', descKey: 'cl.photo_detail_desc', required: false },
];
import { listingsAPI, photoAPI, marketAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const CONDITION_OPTIONS = [
  { key: 'new_tag', score: 10, color: 'emerald', badge: 'bg-emerald-500' },
  { key: 'new_no_tag', score: 9, color: 'green', badge: 'bg-green-500' },
  { key: 'very_good', score: 7, color: 'blue', badge: 'bg-blue-500' },
  { key: 'good', score: 5, color: 'amber', badge: 'bg-amber-500' },
  { key: 'fair', score: 3, color: 'orange', badge: 'bg-orange-500' },
];

const DAMAGE_OPTIONS = [
  { key: 'none' },
  { key: 'scratches' },
  { key: 'dents' },
  { key: 'paint_peeling' },
  { key: 'grip_worn' },
  { key: 'surface_cracks' },
  { key: 'edge_damage' },
];

const BRANDS_DISPLAY = {
  adidas: 'Adidas', babolat: 'Babolat', bullpadel: 'Bullpadel', 'drop shot': 'Drop Shot',
  dunlop: 'Dunlop', head: 'Head', nox: 'Nox', siux: 'Siux', starvie: 'StarVie', 
  technifibre: 'Tecnifibre', varlion: 'Varlion', wilson: 'Wilson', autre: 'Autre',
};

const stripBrand = (model, brand) => {
  if (!model || !brand) return model;
  const regex = new RegExp('^' + brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i');
  return model.replace(regex, '');
};

const CUSTOM_MODEL_KEY = '__custom__';

const CreateListingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [brandsModels, setBrandsModels] = useState({});
  const [availableModels, setAvailableModels] = useState([]);
  const [modelOpen, setModelOpen] = useState(false);
  const [customModel, setCustomModel] = useState(false);
  const [photoSlots, setPhotoSlots] = useState({ front: null, back: null, profile: null, grip: null, detail: null });
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [suggestedPrice, setSuggestedPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sourceUrl, setSourceUrl] = useState(''); // Admin only - Leboncoin URL

  const isAdmin = user?.is_admin === true;

  const [form, setForm] = useState({
    brand: '', model: '', description: '', price: '',
    condition_score: 0, shape: '', weight: '', play_type: '',
    condition_key: '', damages: [], year_of_purchase: new Date().getFullYear(),
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    marketAPI.getBrandsModels().then(res => setBrandsModels(res.data)).catch(() => {});
  }, [isAuthenticated, authLoading, navigate]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    if (form.brand && form.brand !== 'autre' && brandsModels[form.brand.toLowerCase()]) {
      setAvailableModels(brandsModels[form.brand.toLowerCase()] || []);
      setCustomModel(false);
    } else {
      setAvailableModels([]);
      setCustomModel(form.brand === 'autre');
    }
    setForm(prev => ({ ...prev, model: '' }));
  }, [form.brand, brandsModels]);

  // Auto-detect shape and weight when model is selected
  useEffect(() => {
    if (form.brand && form.model) {
      marketAPI.getModelSpecs({ brand: form.brand, model: form.model })
        .then(res => {
          const { shape, weight } = res.data;
          setForm(prev => ({
            ...prev,
            shape: shape || prev.shape,
            weight: weight || prev.weight,
            play_type: res.data.play_type || prev.play_type,
          }));
        })
        .catch(() => {});
    }
  }, [form.brand, form.model]);

  useEffect(() => {
    if (form.brand && form.model && form.condition_score) {
      marketAPI.getPriceSuggestion({ brand: form.brand, model: form.model, condition_score: form.condition_score })
        .then(res => setSuggestedPrice(res.data))
        .catch(() => setSuggestedPrice(null));
    }
  }, [form.brand, form.model, form.condition_score]);

  const handleSlotUpload = async (slotKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSlot(slotKey);
    try {
      const res = await photoAPI.upload(file);
      setPhotoSlots(prev => ({ ...prev, [slotKey]: { id: res.data.id, url: photoAPI.getUrl(res.data.id) } }));
    } catch { toast.error(t('cl.upload_error')); }
    setUploadingSlot(null);
  };

  const removeSlotPhoto = (slotKey) => setPhotoSlots(prev => ({ ...prev, [slotKey]: null }));

  const filledRequiredSlots = PHOTO_SLOTS.filter(s => s.required && photoSlots[s.key]).length;
  const allRequiredFilled = filledRequiredSlots === 4;

  const selectCondition = (option) => {
    setForm(prev => ({ ...prev, condition_score: option.score, condition_key: option.key }));
  };

  const toggleDamage = (key) => {
    if (key === 'none') {
      setForm(prev => ({ ...prev, damages: prev.damages.includes('none') ? [] : ['none'] }));
    } else {
      setForm(prev => ({
        ...prev,
        damages: prev.damages.includes(key)
          ? prev.damages.filter(d => d !== key)
          : [...prev.damages.filter(d => d !== 'none'), key]
      }));
    }
  };

  const handleSubmit = async () => {
    const allPhotos = PHOTO_SLOTS.map(s => photoSlots[s.key]).filter(Boolean);
    if (!form.brand || !form.model || !form.price || !allRequiredFilled) {
      toast.error(t('cl.fill_all'));
      return;
    }
    setSubmitting(true);
    try {
      const submitData = { ...form, price: parseFloat(form.price) };
      if (form.brand === 'autre' && form.custom_brand) {
        submitData.brand = form.custom_brand;
      }
      delete submitData.custom_brand;
      // Admin can add source URL
      if (isAdmin && sourceUrl.trim()) {
        submitData.source_url = sourceUrl.trim();
      }
      const res = await listingsAPI.create(submitData);
      await listingsAPI.updatePhotos(res.data.id, allPhotos.map(p => p.id));
      toast.success(t('cl.submitted'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('cl.error_publish'));
    } finally { setSubmitting(false); }
  };

  const canGoNext = () => {
    if (step === 1) {
      if (form.brand === 'autre') return form.custom_brand && form.model && form.shape && form.weight && form.play_type;
      return form.brand && form.model && form.shape && form.weight && form.play_type;
    }
    if (step === 2) return form.condition_key && form.damages.length > 0 && allRequiredFilled;
    if (step === 3) return form.price && parseFloat(form.price) > 0;
    return false;
  };

  if (authLoading) return null;

  const selectedModelClean = stripBrand(form.model, BRANDS_DISPLAY[form.brand?.toLowerCase()] || form.brand);
  const brandDisplay = form.brand === 'autre' ? (form.custom_brand || t('cl.brand_other')) : BRANDS_DISPLAY[form.brand];

  return (
    <div className="min-h-screen bg-[#F0F7FA]">
      <div className="bg-gradient-to-r from-[#033871] to-[#0C63BF] py-8 text-center">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white">{t('cl.title')}</h1>
        <p className="text-[#B8D4E3] mt-1">{t('cl.subtitle')}</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 pb-12">
        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[{ n: 1, label: t('cl.step_racket') }, { n: 2, label: t('cl.step_condition') }, { n: 3, label: t('cl.step_price') }].map((s, idx) => (
            <React.Fragment key={s.n}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors
                  ${step >= s.n ? 'bg-[#0C63BF] text-white' : 'bg-[#D6E8F0] text-[#5A8BA8]'}`}>
                  {step > s.n ? <Check className="w-5 h-5" /> : s.n}
                </div>
                <span className={`text-xs mt-1 ${step >= s.n ? 'text-[#0C63BF] font-medium' : 'text-[#B8D4E3]'}`}>{s.label}</span>
              </div>
              {idx < 2 && <div className={`w-12 sm:w-20 h-1 rounded mb-5 ${step > s.n ? 'bg-[#0C63BF]' : 'bg-[#D6E8F0]'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-[#D6E8F0] shadow-sm overflow-hidden">
          {/* ====== STEP 1 ====== */}
          {step === 1 && (
            <div className="p-6 space-y-6" data-testid="step-1">
              <h2 className="text-lg font-bold text-[#033871]">{t('cl.step1_title')}</h2>

              {/* Brand selection - visual cards */}
              <div>
                <Label className="text-sm font-semibold text-[#033871] mb-3 block">{t('cl.choose_brand')}</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {Object.entries(BRANDS_DISPLAY).map(([key, name]) => (
                    <button key={key} type="button" onClick={() => setForm(prev => ({ ...prev, brand: key }))}
                      className={`p-2.5 sm:p-3 rounded-xl border-2 text-center transition-all text-xs sm:text-sm font-medium ${
                        form.brand === key
                          ? 'border-[#0C63BF] bg-[#E8F4FD] text-[#033871] shadow-sm'
                          : 'border-[#E8F4FD] bg-white text-[#5A8BA8] hover:border-[#B8D4E3]'
                      }`} data-testid={`brand-${key}`}>
                      {key === 'autre' ? t('cl.brand_other') : name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Model selection - combobox */}
              {form.brand && form.brand !== 'autre' && (
                <div className="animate-in slide-in-from-top-2">
                  <Label className="text-sm font-semibold text-[#033871] mb-2 block">
                    {t('cl.which_model', { brand: BRANDS_DISPLAY[form.brand] })}
                  </Label>

                  {!customModel ? (
                    <>
                      <Popover open={modelOpen} onOpenChange={setModelOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-between font-normal h-12 text-base focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="model-select">
                            {selectedModelClean || t('cl.search_model')}
                            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t('cl.search_placeholder', { brand: BRANDS_DISPLAY[form.brand] })} className="focus:ring-0 focus:outline-none" />
                            <CommandList>
                              <CommandEmpty>{t('cl.no_model_found')}</CommandEmpty>
                              <CommandGroup heading={t('cl.models_count', { count: availableModels.length })}>
                                {availableModels.map(m => {
                                  const cleanName = stripBrand(m.display, BRANDS_DISPLAY[form.brand] || form.brand);
                                  return (
                                    <CommandItem key={m.key} value={m.display}
                                      onSelect={v => { setForm(prev => ({ ...prev, model: v })); setModelOpen(false); }}>
                                      <Check className={`mr-2 h-4 w-4 ${form.model === m.display ? 'opacity-100' : 'opacity-0'}`} />
                                      {cleanName}
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <button
                        type="button"
                        onClick={() => { setCustomModel(true); setForm(prev => ({ ...prev, model: '' })); }}
                        className="mt-2 text-xs text-[#0C63BF] hover:underline"
                        data-testid="custom-model-toggle"
                      >
                        {t('cl.not_in_list')}
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={form.model}
                        onChange={e => setForm(prev => ({ ...prev, model: e.target.value }))}
                        placeholder={t('cl.custom_model_ph', { brand: BRANDS_DISPLAY[form.brand] })}
                        className="w-full h-12 px-4 rounded-xl border-2 border-[#D6E8F0] text-base text-[#033871] focus:ring-2 focus:ring-[#0C63BF] focus:border-transparent"
                        data-testid="custom-model-input"
                      />
                      <button
                        type="button"
                        onClick={() => { setCustomModel(false); setForm(prev => ({ ...prev, model: '' })); }}
                        className="mt-2 text-xs text-[#0C63BF] hover:underline"
                        data-testid="back-to-list-toggle"
                      >
                        {t('cl.back_to_list')}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Custom brand + model for "Autre" */}
              {form.brand === 'autre' && (
                <div className="animate-in slide-in-from-top-2 space-y-4">
                  <div>
                    <Label className="text-sm font-semibold text-[#033871] mb-2 block">{t('cl.brand_name')}</Label>
                    <input
                      type="text"
                      value={form.custom_brand || ''}
                      onChange={e => setForm(prev => ({ ...prev, custom_brand: e.target.value }))}
                      placeholder={t('cl.brand_ph')}
                      className="w-full h-12 px-4 rounded-xl border-2 border-[#D6E8F0] text-base text-[#033871] focus:ring-2 focus:ring-[#0C63BF] focus:border-transparent"
                      data-testid="custom-brand-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#033871] mb-2 block">{t('cl.model_name')}</Label>
                    <input
                      type="text"
                      value={form.model}
                      onChange={e => setForm(prev => ({ ...prev, model: e.target.value }))}
                      placeholder={t('cl.model_ph')}
                      className="w-full h-12 px-4 rounded-xl border-2 border-[#D6E8F0] text-base text-[#033871] focus:ring-2 focus:ring-[#0C63BF] focus:border-transparent"
                      data-testid="custom-model-input"
                    />
                  </div>
                </div>
              )}

              {/* Confirmation of selected racket */}
              {form.brand && form.model && (
                <div className="bg-[#E8F4FD] rounded-xl p-4 border border-[#B8D4E3] animate-in slide-in-from-top-2" data-testid="selected-racket">
                  <p className="text-xs text-[#5A8BA8] uppercase tracking-wide">{t('cl.selected_racket')}</p>
                  <p className="text-lg font-bold text-[#033871] mt-1">
                    {brandDisplay} {selectedModelClean}
                  </p>
                </div>
              )}

              {/* Specs */}
              {form.brand && form.model && (
                <div className="space-y-5 animate-in slide-in-from-top-2">
                  <Label className="text-sm font-semibold text-[#033871] block">{t('cl.specs')}</Label>

                  {/* Shape - visual cards */}
                  <div>
                    <p className="text-sm text-[#5A8BA8] mb-2">{t('cl.shape')}</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { value: 'ronde', label: t('cl.shape_round'), desc: t('cl.shape_round_desc'), icon: CircleDot },
                        { value: 'diamant', label: t('cl.shape_diamond'), desc: t('cl.shape_diamond_desc'), icon: Diamond },
                        { value: 'goutte', label: t('cl.shape_drop'), desc: t('cl.shape_drop_desc'), icon: Droplet },
                      ].map(s => (
                        <button key={s.value} type="button" onClick={() => setForm(prev => ({ ...prev, shape: s.value }))}
                          className={`p-4 rounded-xl border-2 text-center transition-all ${
                            form.shape === s.value
                              ? 'border-[#0C63BF] bg-[#E8F4FD]'
                              : 'border-[#E8F4FD] hover:border-[#B8D4E3]'
                          }`} data-testid={`shape-${s.value}`}>
                          <s.icon className={`w-6 h-6 mx-auto mb-1 ${form.shape === s.value ? 'text-[#0C63BF]' : 'text-[#B8D4E3]'}`} />
                          <p className={`font-semibold text-sm ${form.shape === s.value ? 'text-[#033871]' : 'text-[#5A8BA8]'}`}>{s.label}</p>
                          <p className="text-xs text-[#B8D4E3] mt-0.5">{s.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Weight & Play type side by side */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#5A8BA8] mb-2">{t('cl.weight')}</p>
                      <Select value={form.weight} onValueChange={v => setForm(prev => ({ ...prev, weight: v }))}>
                        <SelectTrigger className="h-11" data-testid="weight-select"><SelectValue placeholder={t('cl.select')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="340-355g">{t('cl.weight_light')}</SelectItem>
                          <SelectItem value="355-370g">{t('cl.weight_medium')}</SelectItem>
                          <SelectItem value="370-390g">{t('cl.weight_heavy')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-sm text-[#5A8BA8] mb-2">{t('cl.play_type')}</p>
                      <Select value={form.play_type} onValueChange={v => setForm(prev => ({ ...prev, play_type: v }))}>
                        <SelectTrigger className="h-11" data-testid="play-type-select"><SelectValue placeholder={t('cl.select')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="control">{t('cl.play_control')}</SelectItem>
                          <SelectItem value="puissance">{t('cl.play_power')}</SelectItem>
                          <SelectItem value="polyvalent">{t('cl.play_versatile')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-[#5A8BA8] mb-2">{t('cl.year')}</p>
                    <Select value={String(form.year_of_purchase)} onValueChange={v => setForm(prev => ({ ...prev, year_of_purchase: parseInt(v) }))}>
                      <SelectTrigger className="h-11" data-testid="year-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2026, 2025, 2024, 2023, 2022, 2021, 2020].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ====== STEP 2 ====== */}
          {step === 2 && (
            <div className="p-6 space-y-8" data-testid="step-2">
              <h2 className="text-lg font-bold text-[#033871]">{t('cl.step2_title', { racket: `${brandDisplay} ${selectedModelClean}` })}</h2>

              {/* Condition selection */}
              <div>
                <Label className="text-sm font-semibold text-[#033871] mb-4 block">{t('cl.condition_q')}</Label>
                <div className="space-y-2.5">
                  {CONDITION_OPTIONS.map(opt => {
                    const selected = form.condition_key === opt.key;
                    return (
                      <button key={opt.key} type="button" onClick={() => selectCondition(opt)}
                        className={`w-full p-4 rounded-xl text-left transition-all border-2 flex items-center gap-3 ${
                          selected ? 'border-[#0C63BF] bg-[#0C63BF]/5' : 'bg-white border-[#E8F4FD] hover:border-[#B8D4E3]'
                        }`} data-testid={`condition-${opt.key}`}>
                        <span className={`w-8 h-8 rounded-lg ${opt.badge} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                          {opt.score}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-[#033871]">{t(`cl.cond_${opt.key}`)}</p>
                          <p className="text-xs text-[#5A8BA8]">{t(`cl.cond_${opt.key}_desc`)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Separator */}
              <div className="border-t border-[#E8F4FD]" />

              {/* Damages */}
              <div>
                <Label className="text-sm font-semibold text-[#033871] mb-4 block">{t('cl.damages_title')}</Label>
                <div className="grid grid-cols-2 gap-3">
                  {DAMAGE_OPTIONS.map(d => (
                    <button key={d.key} type="button" onClick={() => toggleDamage(d.key)}
                      className={`p-3 rounded-xl text-sm text-left transition-all border-2 ${
                        form.damages.includes(d.key)
                          ? d.key === 'none' ? 'bg-green-50 border-green-400 text-green-700 font-medium' : 'bg-red-50 border-red-300 text-red-700 font-medium'
                          : 'bg-white border-[#E8F4FD] text-[#5A8BA8] hover:border-[#B8D4E3]'
                      }`} data-testid={`damage-${d.key}`}>
                      <span className="flex items-center gap-2">
                        {form.damages.includes(d.key) && <Check className="w-4 h-4 shrink-0" />}
                        {t(`cl.dmg_${d.key}`)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Separator */}
              <div className="border-t border-[#E8F4FD]" />

              {/* Photos */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-semibold text-[#033871]">{t('cl.photos')}</Label>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${allRequiredFilled ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                    {t('cl.required_count', { n: filledRequiredSlots })}
                  </span>
                </div>
                <div className="bg-[#F0F7FA] rounded-xl p-3 mb-4 flex items-start gap-2">
                  <Info className="w-4 h-4 text-[#0C63BF] mt-0.5 shrink-0" />
                  <p className="text-xs text-[#5A8BA8]">{t('cl.photo_hint')}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {PHOTO_SLOTS.map(slot => {
                    const photo = photoSlots[slot.key];
                    const isUploading = uploadingSlot === slot.key;
                    return (
                      <div key={slot.key} className="flex flex-col" data-testid={`photo-slot-${slot.key}`}>
                        {photo ? (
                          <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-green-400 bg-green-50">
                            <img src={photo.url} alt={t(slot.labelKey)} className="w-full h-full object-cover" />
                            <button onClick={() => removeSlotPhoto(slot.key)}
                              className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow"
                              data-testid={`remove-photo-${slot.key}`}>
                              <X className="w-3 h-3" />
                            </button>
                            <div className="absolute bottom-0 inset-x-0 bg-green-500/90 text-white text-[10px] font-bold text-center py-1">
                              <Check className="w-3 h-3 inline mr-0.5" />{t(slot.labelKey)}
                            </div>
                          </div>
                        ) : (
                          <label className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                            slot.required ? 'border-[#0C63BF]/40 hover:border-[#0C63BF] hover:bg-[#E8F4FD]' : 'border-[#B8D4E3] hover:border-[#5A8BA8] hover:bg-[#F0F7FA]'
                          }`} data-testid={`upload-photo-${slot.key}`}>
                            <input type="file" accept="image/*" onChange={(e) => handleSlotUpload(slot.key, e)} className="hidden" />
                            {isUploading ? (
                              <Loader2 className="w-6 h-6 text-[#0C63BF] animate-spin" />
                            ) : (
                              <>
                                <Camera className={`w-6 h-6 mb-1 ${slot.required ? 'text-[#0C63BF]/50' : 'text-[#B8D4E3]'}`} />
                                <span className="text-xs font-bold text-[#033871]">{t(slot.labelKey)}</span>
                                <span className="text-[10px] text-[#5A8BA8]">{t(slot.descKey)}</span>
                              </>
                            )}
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ====== STEP 3 ====== */}
          {step === 3 && (
            <div className="p-6 space-y-6" data-testid="step-3">
              <h2 className="text-lg font-bold text-[#033871]">{t('cl.step3_title')}</h2>

              {/* Price suggestion */}
              {suggestedPrice?.suggested_price && (
                <div className="bg-gradient-to-r from-[#E8F4FD] to-[#F0F7FA] rounded-xl p-5 border border-[#B8D4E3]" data-testid="price-suggestion">
                  <p className="text-xs text-[#5A8BA8] uppercase tracking-wide font-medium">{t('cl.suggested')}</p>
                  <p className="text-3xl font-black text-[#0C63BF] mt-2">{suggestedPrice.suggested_price} &euro;</p>
                  <p className="text-sm text-[#5A8BA8] mt-1">
                    {t('cl.new_value', { price: suggestedPrice.market_price, score: form.condition_score })}
                  </p>
                </div>
              )}

              {/* Price input */}
              <div>
                <Label className="text-sm font-semibold text-[#033871]">{t('cl.your_price')}</Label>
                <div className="relative mt-2">
                  <input type="number" step="1" min="1" value={form.price}
                    onChange={e => setForm(prev => ({ ...prev, price: e.target.value }))}
                    className="w-full h-14 pl-5 pr-14 rounded-xl border-2 border-[#D6E8F0] text-2xl font-black text-[#033871] focus:ring-2 focus:ring-[#0C63BF] focus:border-transparent"
                    placeholder={suggestedPrice?.suggested_price ? String(suggestedPrice.suggested_price) : "0"} data-testid="price-input" />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xl text-[#5A8BA8] font-bold">&euro;</span>
                </div>
                {form.price && parseFloat(form.price) > 0 && (
                  <div className="mt-3 bg-[#F0F7FA] rounded-lg p-3 text-sm">
                    <div className="flex justify-between font-bold text-[#033871]">
                      <span>{t('cl.you_receive')}</span>
                      <span className="text-[#32CD32]">{Math.round(parseFloat(form.price))} &euro;</span>
                    </div>
                    <p className="text-[10px] text-[#5A8BA8] mt-1">{t('cl.no_commission')}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm font-semibold text-[#033871]">{t('cl.description')}</Label>
                <textarea value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3} placeholder={t('cl.desc_ph')}
                  className="w-full mt-2 p-3 rounded-xl border-2 border-[#D6E8F0] text-sm resize-none focus:ring-2 focus:ring-[#0C63BF] focus:border-transparent"
                  data-testid="description-input" />
              </div>

              {/* Admin only - Source URL */}
              {isAdmin && (
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <Label className="text-sm font-semibold text-purple-700 flex items-center gap-2">
                    <Link2 className="w-4 h-4" /> {t('cl.source_url')}
                  </Label>
                  <input 
                    type="url" 
                    value={sourceUrl} 
                    onChange={e => setSourceUrl(e.target.value)}
                    placeholder="https://www.leboncoin.fr/..."
                    className="w-full mt-2 h-10 px-3 rounded-lg border border-purple-200 text-sm focus:border-purple-400 outline-none"
                    data-testid="source-url-input" 
                  />
                  <p className="text-xs text-purple-500 mt-1">{t('cl.source_url_hint')}</p>
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between p-6 pt-0">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="gap-2 h-11" data-testid="prev-step">
                <ArrowLeft className="w-4 h-4" /> {t('cl.back')}
              </Button>
            ) : <div />}

            {step < 3 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canGoNext()} className="bg-[#0C63BF] hover:bg-[#033871] text-white gap-2 h-11 disabled:bg-[#B8D4E3] disabled:text-white" data-testid="next-step">
                {t('cl.next')} <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting || !canGoNext()} className="bg-[#32CD32] hover:bg-[#28a428] gap-2 h-11 px-8" data-testid="publish-btn">
                {submitting ? t('cl.publishing') : t('cl.publish')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateListingPage;
